import { useCallback, useEffect, useRef, useState } from 'react';
import { ActionType, DomPosition, type Chart, type Indicator } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { INDICATOR_DEFAULT_COLORS } from '@/components/Chart/indicatorDefs';
import { MACRO_IND_PREFIX } from '@/indicators/macroOverlay';
import { Z_INDEX } from '@/constants/zIndex';

// klinecharts' own default line palette (getDefaultIndicatorStyle().lines) — used only as
// a last-resort fallback when an indicator has no colour override applied at all.
const IND_LINE_COLORS = ['#FF9600', '#935EBD', '#1677FF', '#E11D74', '#01C5C4'];
// klinecharts' default bar up-colour (getDefaultIndicatorStyle().bars[0].upColor) — used for
// bar/histogram figures (e.g. MACD's histogram), which this app never recolours individually.
const DEFAULT_BAR_UP_COLOR = 'rgba(45, 192, 142, .7)';

const BADGE_HEIGHT = 16; // px — min vertical gap enforced between stacked badges
const BADGE_GAP    = 2;

// klinecharts draws its native crosshair price label (unrelated canvas layer) at the exact
// mouse Y on the axis — since that label paints after these DOM badges regardless of z-index
// (see AxisValueLabels doc comment below), any badge within this radius of it would sit behind
// unreadable text. Hide those specific badges instead, rather than fight the stacking order.
const CROSSHAIR_LABEL_EXCLUDE_RADIUS = 16;

type LabelEntry = {
  key: string;
  text: string;
  color: string;
  y: number;
};

function fmtBigNumber(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000)     return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)         return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return n.toFixed(0);
}

function formatValue(value: number, indicator: Indicator): string {
  if (indicator.shouldFormatBigNumber) return fmtBigNumber(value);
  const precision = Number.isFinite(indicator.precision) ? indicator.precision : 2;
  return value.toFixed(Math.max(0, Math.min(8, precision)));
}

// Mirrors ChartContainer's own getRightmostVisibleIndex(): when the user has panned away
// from the live edge, badges should track the indicator's value at the rightmost bar
// currently in view — same convention ChartLegend already uses for its default (non-crosshair)
// values — rather than always freezing on the dataset's absolute last bar.
function getRightmostVisibleIndex(c: Chart): number {
  const range = c.getVisibleRange();
  const dataList = c.getDataList();
  return Math.min(range.to, dataList.length - 1);
}

function labelEntriesEqual(a: LabelEntry[] | undefined, b: LabelEntry[]): boolean {
  if (!a || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (x.key !== y.key || x.text !== y.text || x.color !== y.color || Math.abs(x.y - y.y) > 0.5) return false;
  }
  return true;
}

function panesEqual(a: Map<string, LabelEntry[]>, b: Map<string, LabelEntry[]>): boolean {
  if (a.size !== b.size) return false;
  for (const [paneId, entries] of b) {
    if (!labelEntriesEqual(a.get(paneId), entries)) return false;
  }
  return true;
}

// Greedy 1-D label stacking: sort by natural (value) position, push overlapping
// entries apart symmetrically, then clamp the whole stack within pane bounds.
function declutter(entries: LabelEntry[], top: number, bottom: number): LabelEntry[] {
  if (entries.length <= 1) return entries;
  const sorted = [...entries].sort((a, b) => a.y - b.y);
  const step = BADGE_HEIGHT + BADGE_GAP;

  for (let i = 1; i < sorted.length; i++) {
    const minY = sorted[i - 1].y + step;
    if (sorted[i].y < minY) sorted[i] = { ...sorted[i], y: minY };
  }
  if (sorted[sorted.length - 1].y > bottom) {
    sorted[sorted.length - 1] = { ...sorted[sorted.length - 1], y: bottom };
    for (let i = sorted.length - 2; i >= 0; i--) {
      const maxY = sorted[i + 1].y - step;
      if (sorted[i].y > maxY) sorted[i] = { ...sorted[i], y: maxY };
    }
  }
  return sorted.map((e) => ({ ...e, y: Math.max(top, Math.min(bottom, e.y)) }));
}

// Same idea, but keeps a fixed point (klinecharts' own native last-price marker) immovable
// and stacks the movable entries outward from it on whichever side they naturally fall —
// unlike `declutter`, the anchor itself never gets nudged to make room for anything else.
function declutterAroundAnchor(entries: LabelEntry[], top: number, bottom: number, anchor: number): LabelEntry[] {
  const step = BADGE_HEIGHT + BADGE_GAP;
  const above = entries.filter((e) => e.y <= anchor).sort((a, b) => b.y - a.y); // closest to anchor first
  const below = entries.filter((e) => e.y > anchor).sort((a, b) => a.y - b.y);  // closest to anchor first

  const placed: LabelEntry[] = [];
  let cursor = anchor - step;
  for (const e of above) {
    const y = Math.min(cursor, e.y);
    placed.push({ ...e, y });
    cursor = y - step;
  }
  cursor = anchor + step;
  for (const e of below) {
    const y = Math.max(cursor, e.y);
    placed.push({ ...e, y });
    cursor = y + step;
  }
  return placed.map((e) => ({ ...e, y: Math.max(top, Math.min(bottom, e.y)) }));
}

/**
 * Replaces klinecharts' native indicator last-value axis badges (disabled globally in
 * ChartContainer's applyPersistedChartSettings) with a DOM overlay that positions each
 * badge at its line's real price level, decluttering vertically when values are close.
 */
export function AxisValueLabels() {
  const chart             = useChartStore((s) => s.chart) as Chart | null;
  const indicatorPaneMap  = useChartStore((s) => s.indicatorPaneMap);
  const indicatorColors   = useChartStore((s) => s.indicatorColors);
  const lastBar           = useChartStore((s) => s.lastBar);
  const showLastPriceLine = useChartStore((s) => s.showLastPriceLine);
  const crosshairAxisY    = useChartStore((s) => s.crosshairAxisY);
  const isMobile = useIsMobile();

  const [panes, setPanes] = useState<Map<string, LabelEntry[]>>(new Map());
  const rootRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef(chart);
  useEffect(() => { chartRef.current = chart; }, [chart]);

  const recompute = useCallback(() => {
    const c = chartRef.current;
    if (!c) { setPanes(new Map()); return; }

    const allMap = (c.getIndicatorByPaneId() as unknown) as Map<string, Map<string, Indicator>>;
    const rawByPane    = new Map<string, LabelEntry[]>();
    const boundsByPane = new Map<string, { top: number; height: number }>();
    const rightmostIdx = getRightmostVisibleIndex(c);

    allMap.forEach((indMap, paneId) => {
      const bounding = c.getSize(paneId, DomPosition.Root);
      if (!bounding) return;
      boundsByPane.set(paneId, bounding);

      const raw: LabelEntry[] = [];
      indMap.forEach((ind, name) => {
        if (name.startsWith('COMPARE_') || name.startsWith(MACRO_IND_PREFIX)) return;
        if (!ind.visible) return;
        const idx = rightmostIdx >= 0 ? Math.min(rightmostIdx, ind.result.length - 1) : ind.result.length - 1;
        const data = ind.result[idx];
        if (!data) return;

        // Prefer the colour actually applied to the indicator (via overrideIndicator, which
        // this app calls for every indicator it creates) so the badge always matches the
        // plotted line exactly. Fall back through saved/default app colours, then finally
        // klinecharts' own built-in default — matching what klinecharts itself would render
        // in the rare case no override was ever applied (e.g. an older restored session).
        //
        // VOL is special-cased app-wide (ChartContainer's applyVolStyles / IndicatorBar's
        // creation override): indicatorColors['VOL'] is laid out as [barUp, barDown, ...MA
        // line colours], not one-slot-per-line like every other indicator, so its saved-colour
        // array must be split the same way here or the bar colours leak into the MA line badges.
        const isVol       = name === 'VOL';
        const savedColors = indicatorColors[name] ?? INDICATOR_DEFAULT_COLORS[name] ?? [];
        const savedLineColors = isVol ? savedColors.slice(2) : savedColors;
        const savedBarUpColor = isVol ? savedColors[0] : undefined;
        const overrideLines  = ind.styles?.lines;
        const overrideBars   = ind.styles?.bars;
        const overrideCircle = ind.styles?.circles;
        let lineIdx = 0, barIdx = 0, circleIdx = 0;

        ind.figures.forEach((f) => {
          let color: string;
          if (f.type === 'line') {
            color = overrideLines?.[lineIdx]?.color ?? savedLineColors[lineIdx] ?? IND_LINE_COLORS[lineIdx % IND_LINE_COLORS.length];
            lineIdx++;
          } else if (f.type === 'bar') {
            color = overrideBars?.[barIdx]?.upColor ?? savedBarUpColor ?? DEFAULT_BAR_UP_COLOR;
            barIdx++;
          } else if (f.type === 'circle') {
            color = overrideCircle?.[circleIdx]?.upColor ?? DEFAULT_BAR_UP_COLOR;
            circleIdx++;
          } else {
            return; // figure type klinecharts itself wouldn't badge either
          }
          if (typeof f.title !== 'string' || f.title.length === 0) return;

          const value = data[f.key];
          if (typeof value !== 'number' || !Number.isFinite(value)) return;
          const px = c.convertToPixel({ value }, { paneId, absolute: true }) as { y?: number };
          if (typeof px.y !== 'number') return;
          raw.push({ key: `${paneId}:${name}:${f.key}`, text: formatValue(value, ind), color, y: px.y });
        });
      });

      if (raw.length > 0) rawByPane.set(paneId, raw);
    });

    // klinecharts' own native last-price marker (dashed line + badge) is fixed at the true
    // close price and isn't something we draw — but our badges must stack around it rather
    // than over it, so resolve its pixel Y as an immovable anchor for the price pane.
    let lastPriceAnchor: number | null = null;
    if (showLastPriceLine && lastBar) {
      let bounding = boundsByPane.get('candle_pane');
      if (!bounding) {
        const b = c.getSize('candle_pane', DomPosition.Root);
        if (b) { bounding = b; boundsByPane.set('candle_pane', b); }
      }
      if (bounding) {
        const px = c.convertToPixel({ value: lastBar.close }, { paneId: 'candle_pane', absolute: true }) as { y?: number };
        if (typeof px.y === 'number') lastPriceAnchor = px.y;
      }
    }

    const next = new Map<string, LabelEntry[]>();
    rawByPane.forEach((raw, paneId) => {
      const bounding = boundsByPane.get(paneId);
      if (!bounding) return;
      // klinecharts' pane Bounding never populates `.bottom` (only top/height/width/left
      // are set by the layout engine) — derive the pane's bottom edge from top+height instead.
      const paneBottom = bounding.top + bounding.height;
      const clampTop = bounding.top + BADGE_HEIGHT / 2;
      const clampBottom = paneBottom - BADGE_HEIGHT / 2;
      const entries = paneId === 'candle_pane' && lastPriceAnchor != null
        ? declutterAroundAnchor(raw, clampTop, clampBottom, lastPriceAnchor)
        : declutter(raw, clampTop, clampBottom);
      next.set(paneId, entries);
    });

    setPanes((prev) => (panesEqual(prev, next) ? prev : next));
  }, [indicatorColors, lastBar, showLastPriceLine]);

  useEffect(() => {
    if (!chart) return;
    recompute();
    chart.subscribeAction(ActionType.OnPaneDrag, recompute);
    chart.subscribeAction(ActionType.OnVisibleRangeChange, recompute);

    let ro: ResizeObserver | null = null;
    if (rootRef.current) {
      ro = new ResizeObserver(recompute);
      ro.observe(rootRef.current);
    }
    return () => {
      chart.unsubscribeAction(ActionType.OnPaneDrag, recompute);
      chart.unsubscribeAction(ActionType.OnVisibleRangeChange, recompute);
      ro?.disconnect();
    };
  }, [chart, recompute]);

  // klinecharts doesn't dispatch a subscribable action for Y-axis price-scale dragging (or
  // for the candle pane's own vertical drag-to-rescale) — it just mutates the axis range and
  // repaints its canvas directly. Track pointer activity over the chart and recompute every
  // frame while a pointer is down anywhere in it, so badges stay pinned to their price level
  // through any drag, not just the x-axis pan/zoom gestures that do fire an action.
  //
  // Mouse-wheel Y-axis scaling is a *third* untracked path: ChartContainer's own `onWheel`
  // handler reaches straight into the axis component and repaints, again with no action fired.
  // That handler sits on containerRef.current — the element the app passed to init() — and
  // calls stopPropagation() in the Y-axis-scroll case. chart.getDom() is NOT that element:
  // klinecharts creates its own inner wrapper div as _chartContainer and getDom() returns
  // that, a *child* of containerRef.current (see _initContainer in klinecharts' source).
  // Since capture phase runs root-down, containerRef.current's listener fires before the event
  // ever reaches that inner child, so a listener there never even sees the event once
  // stopPropagation() is called. window is the one node guaranteed to come first in every
  // event's path, so listening there in the capture phase is the only way to reliably observe
  // it regardless of which nested element the app's handler stops propagation at.
  useEffect(() => {
    if (!chart) return;
    const container = chart.getDom();
    if (!container) return;

    let rafId: number | null = null;
    const loop = () => { recompute(); rafId = requestAnimationFrame(loop); };
    const start = () => { if (rafId == null) rafId = requestAnimationFrame(loop); };
    const stop = () => {
      if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
      recompute();
    };
    const onWheel = () => {
      // The app's own handler (deeper in the capture path) mutates the axis range
      // synchronously but hasn't run yet at this point — only the deferred call is
      // guaranteed to observe the post-scroll range.
      requestAnimationFrame(recompute);
      requestAnimationFrame(() => requestAnimationFrame(recompute));
    };

    container.addEventListener('pointerdown', start);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    window.addEventListener('wheel', onWheel, { passive: true, capture: true });
    return () => {
      container.removeEventListener('pointerdown', start);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      window.removeEventListener('wheel', onWheel, { capture: true });
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [chart, recompute]);

  // Re-run whenever indicators are added/removed/toggled — klinecharts needs one render
  // cycle to materialise new indicator results before reading them back.
  useEffect(() => {
    const id = requestAnimationFrame(recompute);
    return () => cancelAnimationFrame(id);
  }, [indicatorPaneMap, recompute]);

  // On mobile the y-axis is too narrow for value-positioned badges — ChartLegend already
  // renders a compact stacked-list fallback there (renderPaneAxisValues).
  if (isMobile) return null;

  return (
    <div ref={rootRef} style={{ position: 'absolute', inset: 0, zIndex: Z_INDEX.chartOverlay + 1, pointerEvents: 'none' }}>
      {[...panes.entries()].flatMap(([paneId, entries]) =>
        entries
          .filter((e) => (
            crosshairAxisY?.paneId !== paneId
            || Math.abs(e.y - crosshairAxisY.y) >= CROSSHAIR_LABEL_EXCLUDE_RADIUS
          ))
          .map((e) => (
          <span
            key={e.key}
            data-pane={paneId}
            style={{
              position: 'absolute',
              right: 0,
              top: e.y - BADGE_HEIGHT / 2,
              height: BADGE_HEIGHT,
              lineHeight: `${BADGE_HEIGHT}px`,
              fontSize: 11,
              fontFamily: 'monospace',
              color: '#fff',
              background: e.color,
              padding: '0 4px',
              whiteSpace: 'nowrap',
            }}
          >
            {e.text}
          </span>
        )),
      )}
    </div>
  );
}
